import { useEffect, useState, type ReactNode } from "react";

/**
 * Renders its children only after the browser is idle on the client.
 *
 * Used to defer non-critical, client-only widgets (chat, exit-intent popup,
 * scroll-to-top, analytics) out of the initial render so their JS chunks load
 * and execute after the page is interactive — reducing main-thread JS work and
 * TBT on first load. Returns null during SSR and the first client render.
 *
 * `phase` σπάει τα deferred widgets σε διαδοχικά «κύματα»: κάθε φάση περιμένει
 * μία επιπλέον idle περίοδο, ώστε να μη γίνεται ένα μεγάλο task που mountάρει
 * τα πάντα μαζί (long task στο κύριο νήμα).
 */
export function DeferredMount({
  children,
  phase = 0,
}: {
  children: ReactNode;
  phase?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const w = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const step = (remaining: number) => {
      if (cancelled) return;
      if (remaining <= 0) {
        setReady(true);
        return;
      }
      if (typeof w.requestIdleCallback === "function") {
        idleId = w.requestIdleCallback(() => step(remaining - 1), { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(() => step(remaining - 1), 400);
      }
    };

    step(phase + 1);

    return () => {
      cancelled = true;
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [phase]);

  if (!ready) return null;
  return <>{children}</>;
}
